
import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Typography, TextField, Button, CircularProgress, Divider, Stack } from '@mui/material';
import { SurfaceCard } from '../components/ui/SurfaceCard';
import type { SignInOutput } from 'aws-amplify/auth';

/** Official multicolor Google "G" (brand guidelines require the colored mark). */
const GoogleG: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden focusable="false">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export const LoginView = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPasswordValue, setConfirmNewPasswordValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const { signIn, signInWithGoogle, isAuthenticated, confirmNewPassword } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Add a useEffect to redirect if already authenticated and on the login page
  useEffect(() => {
    if (isAuthenticated && window.location.pathname === '/login') {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result: SignInOutput = await signIn(email, password);
      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNewPasswordRequired(true);
      } else if (isAuthenticated) {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmNewPasswordValue) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await confirmNewPassword(newPassword);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to set new password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
      // The navigation will be handled by the AuthProvider on successful sign-in
    } catch (err: any) {
      setError(err.message || 'Failed to sign in with Google.');
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 6,
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Gold ambience (same glow as the dashboard) */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "50%",
          top: { xs: "4%", md: "-6%" },
          width: { xs: 680, md: 1150 },
          height: { xs: 620, md: 820 },
          transform: "translateX(-50%)",
          background: "radial-gradient(closest-side, rgba(205,160,117,0.28), rgba(205,160,117,0) 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        component="img"
        src="/transparentLogo.png"
        alt=""
        aria-hidden
        sx={{
          position: "absolute",
          top: -60,
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(1400px, 160vw)",
          opacity: 0.45,
          pointerEvents: "none",
          userSelect: "none",
          // Dissolve the monogram's edges so it reads as ambience, never as a cut-off header.
          maskImage: "radial-gradient(ellipse 60% 55% at 50% 55%, #000 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 60% 55% at 50% 55%, #000 30%, transparent 75%)",
        }}
      />
      <Box
        component="img"
        src="/circles.png"
        alt=""
        aria-hidden
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: { xs: "180%", md: "80%" },
          transform: "translate(25%, 55%)",
          opacity: 0.5,
          pointerEvents: "none",
          userSelect: "none",
        }}
      />

      <Box sx={{ width: "100%", maxWidth: 400, position: "relative" }}>
        <Stack alignItems="center" spacing={1.5} sx={{ mb: 3.5 }}>
          <img src="/logo.png" alt="Teks" height={32} />
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: "text.secondary",
              pl: "0.32em",
            }}
          >
            Liquidity Monitor
          </Typography>
        </Stack>

        <SurfaceCard padding="lg">
          <Typography component="h1" sx={{ fontSize: "1.375rem", fontWeight: 700, letterSpacing: "-0.01em" }}>
            {newPasswordRequired ? 'Set a new password' : 'Sign in'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
            {newPasswordRequired
              ? 'Choose a new password to finish setting up your account.'
              : 'Use your Teks Capital account to continue.'}
          </Typography>

          {newPasswordRequired ? (
            <Box component="form" onSubmit={handleNewPasswordSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  required
                  fullWidth
                  name="newPassword"
                  label="New password"
                  type="password"
                  id="newPassword"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <TextField
                  required
                  fullWidth
                  name="confirmNewPassword"
                  label="Confirm new password"
                  type="password"
                  id="confirmNewPassword"
                  autoComplete="new-password"
                  value={confirmNewPasswordValue}
                  onChange={(e) => setConfirmNewPasswordValue(e.target.value)}
                />
                {error && <Alert severity="error" variant="outlined">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  color="secondary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Set new password'}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleLogin} noValidate>
              <Stack spacing={2}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email address"
                  name="email"
                  type="email"
                  inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {error && <Alert severity="error" variant="outlined">{error}</Alert>}
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  color="secondary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign in'}
                </Button>
                <Divider sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>or</Divider>
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  color="inherit"
                  startIcon={<GoogleG />}
                  onClick={handleGoogleLogin}
                >
                  Continue with Google
                </Button>
              </Stack>
            </Box>
          )}
        </SurfaceCard>
      </Box>
    </Box>
  );
};
